import React, { useState, useEffect } from 'react';
import { getImages } from './api';
import Spinner from './ui/Spinner';
import Alert from './ui/Alert';
import EmptyState from './ui/EmptyState';
import Badge from './ui/Badge';
import UploadPanel from './media/UploadPanel';
import { useToast } from './Toast';
import { mediaLabel, mediaState } from './mediaHelpers';

const PER_PAGE = 10;

/**
 * Modal image picker used by the page editor (main image) and the section
 * editor (image/gallery sections).
 *
 * onSelect receives the raw media item from GET /images — _id,
 * original_filename, name, description, source, contributor_name — because both
 * callers read those exact keys. An upload made from the Upload tab is handed to
 * the same callback: POST /media/upload returns the same shape.
 */
const ImagePicker = ({ onSelect, onClose }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const [tab, setTab] = useState('browse');
  const { addToast } = useToast();

  useEffect(() => {
    if (tab !== 'browse') return;
    const fetchImages = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getImages(page, PER_PAGE, activeSearchQuery);
        setImages(data || {});
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch images');
        setLoading(false);
      }
    };
    fetchImages();
  }, [page, activeSearchQuery, tab]);

  const handleSearch = () => {
    setActiveSearchQuery(searchQuery);
    setPage(1); // Reset to first page when searching
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleSearch();
    }
  };

  const handleSelectImage = (image) => {
    onSelect(image);
  };

  /* The API reports `total` when it can; otherwise a short page means the end. */
  const list = images.media_list || [];
  const hasMore = typeof images.total === 'number'
    ? page * PER_PAGE < images.total
    : list.length >= PER_PAGE;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal modal-lg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="image-picker-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 id="image-picker-title">Choose an image</h3>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="chip-group" style={{ marginBottom: '16px' }}>
            <button
              type="button"
              className={tab === 'browse' ? 'chip active' : 'chip'}
              aria-pressed={tab === 'browse'}
              onClick={() => setTab('browse')}
            >
              Browse library
            </button>
            <button
              type="button"
              className={tab === 'upload' ? 'chip active' : 'chip'}
              aria-pressed={tab === 'upload'}
              onClick={() => setTab('upload')}
            >
              Upload
            </button>
          </div>

          {tab === 'upload' ? (
            <UploadPanel
              autoFocus
              onFinished={({ uploaded, failed, items }) => {
                if (failed) addToast(`${failed} upload${failed === 1 ? '' : 's'} failed`, 'error');
                if (!uploaded) return;
                addToast('Uploaded — thumbnails follow in a few seconds', 'success');
                // Select the first new image straight away; the editor stores the
                // media_id and shows "Processing..." until the copy runs on save.
                handleSelectImage(items[0]);
              }}
            />
          ) : (
            <>
              <div className="toolbar">
                <input
                  type="search"
                  className="input grow"
                  aria-label="Search images"
                  placeholder="Search for images..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <button type="button" className="btn btn-primary" onClick={handleSearch} disabled={loading}>
                  {loading ? 'Searching…' : 'Search'}
                </button>
              </div>

              <Alert type="error" onDismiss={() => setError(null)}>{error}</Alert>

              {loading ? (
                <Spinner label="Loading images…" />
              ) : list.length === 0 ? (
                <EmptyState
                  title={activeSearchQuery ? `No images match "${activeSearchQuery}"` : 'No images found'}
                  message="Switch to the Upload tab to add one without leaving this page."
                  action={
                    <button type="button" className="btn btn-primary" onClick={() => setTab('upload')}>
                      Upload an image
                    </button>
                  }
                />
              ) : (
                <div className="card-grid">
                  {list.map((image) => {
                    const processing = mediaState(image) === 'processing';
                    return (
                      <button
                        key={image._id}
                        type="button"
                        className="card"
                        onClick={() => handleSelectImage(image)}
                        style={{ padding: 0, overflow: 'hidden', cursor: 'pointer', textAlign: 'left', border: '1px solid var(--color-border)' }}
                      >
                        <div style={{ position: 'relative', width: '100%', height: '120px', background: 'var(--color-bg)' }}>
                          {!processing && (
                            <img
                              src={image.thumb_url}
                              alt={mediaLabel(image)}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          {processing && (
                            <div style={{ position: 'absolute', top: '8px', left: '8px' }}>
                              <Badge state="warn">Processing</Badge>
                            </div>
                          )}
                        </div>
                        <div style={{ padding: '8px 10px', fontSize: '13px', fontWeight: 600 }}>
                          {mediaLabel(image)}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="toolbar" style={{ justifyContent: 'center' }}>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setPage(page - 1)} disabled={page === 1 || loading}>
                  Previous
                </button>
                <span className="muted small">
                  Page {page}
                  {typeof images.total === 'number' && ` of ${Math.max(1, Math.ceil(images.total / PER_PAGE))}`}
                </span>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setPage(page + 1)} disabled={!hasMore || loading}>
                  Next
                </button>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default ImagePicker;
